import React, { useRef } from 'react';
import { cn } from '@/lib/utils';
import { useWindowResponsive } from '@/hooks/useWindowResponsive';

interface ResponsiveGridProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  minItemWidth?: number;
  maxColumns?: number;
  gap?: 'none' | 'sm' | 'md' | 'lg' | 'auto';
  aspectRatio?: 'square' | 'video' | 'auto';
}

export function ResponsiveGrid({
  children,
  className,
  minItemWidth = 280,
  maxColumns = 4,
  gap = 'auto',
  aspectRatio = 'auto',
  ...props
}: ResponsiveGridProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const responsive = useWindowResponsive(containerRef);

  // Calculate optimal columns based on container width
  const getOptimalColumns = () => {
    const availableWidth = responsive.width;
    const calculatedColumns = Math.floor(availableWidth / minItemWidth);
    return Math.min(calculatedColumns, maxColumns);
  };

  const columns = getOptimalColumns();

  // Determine gap size
  const actualGap = gap === 'auto' 
    ? responsive.isNarrow ? 'sm' : responsive.isMedium ? 'md' : 'lg'
    : gap;

  const gapClasses = {
    none: 'gap-0',
    sm: 'gap-2',
    md: 'gap-4',
    lg: 'gap-6'
  };

  // Grid template based on calculated columns
  const gridStyle = {
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
  };

  // Aspect ratio classes for grid items
  const aspectRatioClasses = {
    square: '[&>*]:aspect-square',
    video: '[&>*]:aspect-video',
    auto: ''
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        'grid transition-all duration-300 ease-out',
        gapClasses[actualGap],
        aspectRatioClasses[aspectRatio],
        className
      )}
      style={gridStyle}
      {...props}
    >
      {children}
    </div>
  );
}